// @ts-nocheck
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  saveDashboardFiltersAction,
  getDashboardFiltersAction,
  deleteDashboardFiltersAction,
  updateDashboardFiltersAction,
} from "../store/actions/dashboardFilters";

/**
 * Custom hook for managing dashboard filters
 * Provides functions to save, get, and delete dashboard filters
 * Filters are stored per dashboard, allowing each dashboard to have independent filters
 * @param {string} dashboardId - The ID of the dashboard to manage filters for
 */
export const useDashboardFilters = (dashboardId) => {
  const dispatch = useDispatch();
  const {
    originalFilters,
    savedFilters, // Now an object: { "dashboard-id": filters, "another-dashboard": filters, ... }
    isSaving,
    isLoading,
    isDeleting,
    saveError,
    loadError,
    deleteError,
    lastSavedAt,
  } = useSelector((state) => state.dashboardFilters);

  // Get filters for this specific dashboard
  const dashboardSavedFilters = useMemo(() => {
    return dashboardId ? savedFilters?.[dashboardId] : null;
  }, [savedFilters, dashboardId]);

  // Get filters for this specific dashboard
  const dashboardOriginalFilters = useMemo(() => {
    return dashboardId ? originalFilters?.[dashboardId] : null;
  }, [originalFilters, dashboardId]);

  /**
   * Save filters to the backend
   * @param {string} id - Dashboard identifier
   * @param {object} filters - Filter object to save
   * @param {function} successCallback - Callback on success
   * @param {function} failureCallback - Callback on failure
   */
  const saveFilters = useCallback(
    (id, filters, successCallback, failureCallback) => {
      dispatch(
        saveDashboardFiltersAction({
          dashboardId: id,
          filters,
          successCallback,
          failureCallback,
        })
      );
    },
    [dispatch]
  );

  /**
   * Load filters from the backend
   * @param {string} id - Dashboard identifier
   * @param {function} successCallback - Callback on success
   * @param {function} failureCallback - Callback on failure
   */
  const loadFilters = useCallback(
    (id, successCallback, failureCallback) => {
      dispatch(
        getDashboardFiltersAction({
          dashboardId: id,
          successCallback,
          failureCallback,
        })
      );
    },
    [dispatch]
  );

  /**
   * Delete filters from the backend
   * @param {string} id - Dashboard identifier
   * @param {function} successCallback - Callback on success
   * @param {function} failureCallback - Callback on failure
   */
  const deleteFilters = useCallback(
    (id, successCallback, failureCallback) => {
      dispatch(
        deleteDashboardFiltersAction({
          dashboardId: id,
          successCallback,
          failureCallback,
        })
      );
    },
    [dispatch]
  );

  /**
   * Update filters locally in Redux (no API call)
   * @param {string} id - Dashboard identifier
   * @param {object} filters - Filter object to update
   */
  const updateFilters = useCallback(
    (id, filters) => {
      dispatch(
        updateDashboardFiltersAction({
          dashboardId: id,
          filters,
        })
      );
    },
    [dispatch]
  );

  return {
    // State (filtered for this dashboard)
    originalFilters: dashboardOriginalFilters,
    savedFilters: dashboardSavedFilters,
    isSaving,
    isLoading,
    isDeleting,
    saveError,
    loadError,
    deleteError,
    lastSavedAt,
    // Actions
    saveFilters,
    loadFilters,
    deleteFilters,
    updateFilters,
  };
};